import { normalizeAnnouncementRow } from "@/lib/announcementSelectors";
import { logger } from "@/lib/logger";
import { supabase } from "@/lib/supabase";
import type { UserAnnouncementRow } from "@/types/announcements";

const DEFAULT_LIMIT = 50;

/**
 * All calls go through `supabase.rpc` only. User identity = JWT (`auth.uid()` in SECURITY DEFINER
 * functions); no `user_id` argument in the public API.
 *
 * **Client-facing RPCs (4)** — all implemented below
 * - `get_user_announcements_v2` — list + `has_unread_bell` on each row (+ banner_thumbnail_url).
 * - `mark_announcements_shown` — after open dropdown; clears red bell, does not mark items read.
 * - `mark_announcement_read(id)` — one row; read/cutoff logic in Postgres.
 * - `mark_all_announcements_read` — “Mark all as read”.
 *
 * **Server / maintenance (2)** — not called from the app
 * - `purge_old_user_announcements` — e.g. pg_cron weekly.
 * - `is_user_subscribed_to_announcement_stream(uid, sid)` — subscription checks inside other SQL.
 *
 * **Parameter names** in `.rpc({ ... })` must match the deployed `CREATE FUNCTION` arg names. If
 * Supabase returns “unknown argument”, align keys here (e.g. `p_limit` vs `limit`, `id` vs `p_announcement_id`).
 */
export async function fetchUserAnnouncements(
  limit: number = DEFAULT_LIMIT,
): Promise<UserAnnouncementRow[] | null> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      return null;
    }

    const { data, error } = await supabase.rpc("get_user_announcements_v2", {
      p_limit: limit,
    });

    if (error) {
      logger.warn("announcements: get_user_announcements_v2 failed", {
        message: error.message,
        code: error.code,
      });
      return null;
    }

    const rows = (data ?? []) as Partial<UserAnnouncementRow>[];
    return rows
      .filter((r): r is Partial<UserAnnouncementRow> & { id: string | number; title: string } =>
        r != null && r.id != null && typeof r.title === "string",
      )
      .map(normalizeAnnouncementRow);
  } catch (e) {
    logger.warn("announcements: get_user_announcements_v2 exception", e);
    return null;
  }
}

export async function markAnnouncementsShown(): Promise<boolean> {
  try {
    const { error } = await supabase.rpc("mark_announcements_shown");
    if (error) {
      logger.warn("announcements: mark_announcements_shown failed", {
        message: error.message,
        code: error.code,
      });
      return false;
    }
    return true;
  } catch (e) {
    logger.warn("announcements: mark_announcements_shown exception", e);
    return false;
  }
}

export async function markAnnouncementRead(announcementId: string | number): Promise<boolean> {
  try {
    const { error } = await supabase.rpc("mark_announcement_read", {
      p_announcement_id: String(announcementId),
    });
    if (error) {
      logger.warn("announcements: mark_announcement_read failed", {
        message: error.message,
        code: error.code,
      });
      return false;
    }
    return true;
  } catch (e) {
    logger.warn("announcements: mark_announcement_read exception", e);
    return false;
  }
}

export async function markAllAnnouncementsRead(): Promise<boolean> {
  try {
    const { error } = await supabase.rpc("mark_all_announcements_read");
    if (error) {
      logger.warn("announcements: mark_all_announcements_read failed", {
        message: error.message,
        code: error.code,
      });
      return false;
    }
    return true;
  } catch (e) {
    logger.warn("announcements: mark_all_announcements_read exception", e);
    return false;
  }
}
